import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import queryString from 'query-string';
import { Element } from 'react-scroll';
import Highlighter from 'react-highlight-words';
import { TextWrapper } from './Text.style';
import { priorityScrollTo, generateRowId, normalizeString, tryParseJson } from 'lib/utils/helper';
import { findLongestCommonTokenSequence, tokenize } from 'lib/utils/helper/tokenMatcher';

export const Text = ({ children, inheritStyles, section, rowId = '' }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [search, setSearch] = useState('');

  const resetSearchParmas = () => {
    const currentParams = {};

    for (let entry of searchParams.entries()) {
      currentParams[entry[0]] = entry[1];
    }

    setSearchParams({
      ...currentParams,
      search: '',
      rowId: '',
      rowIds: ''
    });

    setSearch('');
  };

  const childrenText = useMemo(() => children?.toString() || '', [children]);
  const [childrenTokens, setChildrenTokens] = useState(null);
  const textRowId = useMemo(() => `${rowId || ''}${generateRowId(section, childrenText)}`, [section, childrenText, rowId]);

  useEffect(() => {
    setChildrenTokens(null);
  }, [childrenText]);

  useEffect(() => {
    const _searchParams = queryString.parse(searchParams.toString());

    let searchStr = _searchParams.search || '';
    const rowIds = [
      ...tryParseJson(_searchParams.rowIds),
      ...(_searchParams.rowId ? [_searchParams.rowId] : [])
    ];

    if (searchStr) {
      setSearch(searchStr);
    }

    if (rowIds?.length && 
      (rowIds.includes(textRowId) || rowIds.includes(rowId)) &&
      !childrenText.includes(searchStr)) {
      if (searchStr.includes(childrenText)) {
        // If rowId is provided and child text is a substring of search, 
        // use child text as search to highlight the exact match instead of the whole search string
        searchStr = childrenText;
        setSearch(searchStr);
      } else {
        // If child text does not include search string, 
        // try to find the longest common token sequence between search string and child text 
        // and use it as search string to highlight
        const searchStrTokens = tokenize(searchStr);
        let childrenTextTokens = childrenTokens;

        if (!childrenTokens) {
          childrenTextTokens = tokenize(childrenText);
          setChildrenTokens(childrenTextTokens);
        }

        const { longestSequence: longestCommonTokens = [] } = findLongestCommonTokenSequence(childrenTextTokens, searchStrTokens);

        if (longestCommonTokens.length) {
          searchStr = childrenText.substring(
            longestCommonTokens[0].index, 
            longestCommonTokens.at(-1).endIndex);
          setSearch(searchStr);
        }
      }
    }

    if (childrenText && searchStr && childrenText.includes(searchStr)) {
      let scrollPriority = rowIds.includes(rowId) ? 0.5 : 1;

      if (_searchParams.section && 
        section && 
        normalizeString(_searchParams.section) !== normalizeString(section)) {
        scrollPriority = 2;
      }
   
      priorityScrollTo(`${textRowId}_highlighted_0`, scrollPriority);
    }
  }, [searchParams, childrenText, section]);

  const HighlightTag = ({ highlightIndex, ...rest }) => {
    const uniqueId = `${textRowId}_highlighted_${highlightIndex}`;

    return (
    <Element className='inline' key={uniqueId} name={uniqueId}>
      <mark className='highlighted' {...rest} />
    </Element>
  )
};

  return (
    <Element key={textRowId} name={textRowId}>
      {inheritStyles ? (<Highlighter
          highlightClassName="highlighted"
          searchWords={[search]}
          autoEscape={true}
          textToHighlight={children}
          highlightTag={HighlightTag}
        />) : (
      <TextWrapper onClick={() => resetSearchParmas()}>
        <Highlighter
          highlightClassName="highlighted"
          searchWords={[search]}
          autoEscape={true}
          textToHighlight={children}
          highlightTag={HighlightTag}
        />
      </TextWrapper>)}
    </Element>
  );
};
